import { API_BASE_URL, api, getAuthToken } from './core';

// Abrechnung nach ADR-0003: Abrechnungsmodell, abrechnende Einheiten, Tarife,
// Vertragskonten und Zahlungsläufe. Der Präfix `/api` ist Pflicht: nginx leitet nur
// `/api/*` an FastAPI `/api/v1/office/*` weiter.
const BASE = '/api/billing';

export type BillingModel = 'periodic' | 'contingent';
export type PeriodAnchor = 'calendar' | 'school_year' | 'contract_start';
export type CancellationRule = 'none' | 'credit' | 'makeup';
export type ContingentMode = 'prepaid' | 'installments' | 'usage_based';
export type TaxMode = 'regelbesteuert' | 'kleinunternehmer' | 'frei_4_21';
export type SepaTransport = 'xml_export' | 'ebics';

export interface BillingSettings {
  enabledModels: BillingModel[];
  defaultModel: BillingModel;
  periodAnchor: PeriodAnchor;
  schoolYearStartMonth: number | null;
  allowedIntervals: number[];
  lessonCancellationRule: CancellationRule;
}

export interface ProviderFlags {
  secretKeyIsSet?: boolean;
  publishableKeyIsSet?: boolean;
  webhookSecretIsSet?: boolean;
  error?: string;
}

export interface BillingEntity {
  id: number;
  name: string;
  legalName: string | null;
  street: string | null;
  zip: string | null;
  city: string | null;
  country: string;
  taxNumber: string | null;
  vatId: string | null;
  taxMode: TaxMode | null;
  sepaTransport: SepaTransport;
  creditorName: string | null;
  creditorId: string | null;
  ibanMasked: string | null;
  bic: string | null;
  mandateRefPrefix: string | null;
  preNotificationDays: number;
  checkoutProvider: CheckoutProvider | null;
  documentKind: DocumentKind | null;
  documentNumberPrefix: string | null;
  documentFooter: string | null;
  /** Eigenes Logo und Farbe auf Beleg und Mahnung; leer = Branding des Mandanten (T-0222). */
  brandLogoUrl: string | null;
  brandColor: string | null;
  dunningEnabled: boolean;
  dunningGraceDays: number;
  dunningIntervalDays: number;
  dunningPayDays: number;
  dunningBlockLevel: number;
  dunningFeeLevel1Cents: number;
  dunningFeeLevel2Cents: number;
  dunningFeeLevel3Cents: number;
  datevConsultantNumber: string | null;
  datevClientNumber: string | null;
  datevChart: string | null;
  datevRevenueAccount: string | null;
  datevFeeAccount: string | null;
  datevBankAccount: string | null;
  datevFiscalYearStartMonth: number | null;
  sepaProblem: string | null;
  isDefault: boolean;
  isActive: boolean;
  standortIds: number[];
  providers: Record<string, ProviderFlags>;
}

export type CheckoutProvider = 'stripe' | 'hausbank_sepa';

export type BillingEntityInput = Partial<
  Omit<BillingEntity, 'id' | 'standortIds' | 'providers' | 'ibanMasked' | 'sepaProblem'>
> & { iban?: string | null };

export interface StripeCredentialsInput {
  secretKey?: string;
  publishableKey?: string;
  webhookSecret?: string;
}

export interface Tariff {
  id: number;
  unterrichtsformId: number;
  standortId: number | null;
  name: string;
  model: BillingModel;
  priceCents: number;
  pricePeriodMonths: number | null;
  allowedIntervals: number[] | null;
  units: number | null;
  contingentMode: ContingentMode | null;
  installmentCount: number | null;
  validFrom: string;
  validTo: string | null;
  isActive: boolean;
  /** Aus einem Paket der Unterrichtsform abgeleitet (Pflege über die Pakete). */
  sourcePackageId?: string | null;
  sourcePayment?: 'immediate' | 'installment' | null;
}

export interface TariffInput {
  unterrichtsformId: number;
  standortId: number | null;
  name: string;
  model: BillingModel;
  priceCents: number;
  pricePeriodMonths?: number;
  allowedIntervals?: number[] | null;
  units?: number;
  contingentMode?: ContingentMode;
  installmentCount?: number;
  validFrom: string;
  validTo?: string | null;
}

/** Altpaket als geplanter bzw. angelegter Kontingent-Tarif (Phase 6). */
export interface LegacyTariffPlan {
  name: string;
  units: number;
  priceCents: number;
  contingentMode: ContingentMode;
  installmentCount: number | null;
  sourcePackageId: string;
  unterrichtsformId: number;
  standortId: number | null;
  reason?: string;
}

export interface LegacyTariffImport {
  dryRun: boolean;
  created: LegacyTariffPlan[];
  skipped: LegacyTariffPlan[];
}

/** Beleg zum Vertragskonto (Phase 7): Rechnung oder Beitragsbescheid. */
export type DocumentKind = 'invoice' | 'contribution_notice';

export interface BillingDocumentItem {
  position: number;
  description: string;
  amountCents: number;
  taxRate: number | null;
  taxCents: number;
  postingDate: string;
  periodStart: string | null;
  periodEnd: string | null;
}

export interface BillingDocument {
  id: number;
  number: string;
  kind: DocumentKind;
  kindLabel: string;
  billingEntityId: number;
  contractAccountId: number;
  debtorId: number;
  documentDate: string;
  periodStart: string;
  periodEnd: string;
  totalCents: number;
  taxCents: number;
  currency: string;
  taxNote: string | null;
  recipientName: string;
  paymentNote: string | null;
  status: 'issued' | 'cancelled';
  cancelReason: string | null;
  fileName: string;
  sentAt: string | null;
  createdAt: string | null;
  items?: BillingDocumentItem[];
}

/** Mahnung (Phase 7): Vorschlag des Mahnlaufs, danach freigegeben. */
export interface DunningNotice {
  id: number;
  number: string | null;
  level: number;
  levelLabel: string;
  status: 'proposed' | 'issued' | 'cancelled';
  billingEntityId: number;
  contractAccountId: number;
  debtorId: number;
  openCents: number;
  feeCents: number;
  oldestDueDate: string | null;
  payUntil: string | null;
  blocksCollection: boolean;
  recipientName: string;
  recipientEmail: string | null;
  fileName: string | null;
  issuedAt: string | null;
  sentAt: string | null;
  cancelReason: string | null;
  createdAt: string | null;
  emailSent?: boolean;
  items?: Array<{ description: string; dueDate: string; openCents: number }>;
}

export interface DunningRunSummary {
  proposals: number;
  byLevel: Record<string, number>;
  openCents: number;
  resetAccounts: number;
}

export interface DocumentRunSummary {
  month: string;
  documents: number;
  totalCents: number;
  numbers: string[];
  skippedAccounts: number;
}

export interface PaymentInstrument {
  id: number;
  provider: string;
  providerRef: string | null;
  type: string;
  displayLabel: string;
  status: string;
  isDefault: boolean;
}

export interface StripeMethodOption {
  paymentMethodId: string;
  type: string;
  brand: string | null;
  last4: string | null;
  isDefault: boolean;
}

export type PostingKind =
  | 'charge'
  | 'credit'
  | 'payment'
  | 'payment_return'
  | 'fee'
  | 'write_off'
  | 'refund'
  | 'reversal';

export interface AccountPosting {
  id: number;
  kind: PostingKind;
  amountCents: number;
  openCents: number;
  postingDate: string;
  dueDate: string;
  description: string;
  sourceType: string;
  reversesPostingId: number | null;
}

export interface ContractAccount {
  id: number;
  accountNumber: string;
  enrollmentId: number;
  debtorId: number;
  billingEntityId: number;
  standortId: number | null;
  subprofileId: string | null;
  billingModel: BillingModel;
  tariffId: number | null;
  priceCents: number | null;
  pricePeriodMonths: number | null;
  intervalMonths: number | null;
  periodAnchor: PeriodAnchor | null;
  anchorMonth: number | null;
  serviceStart: string | null;
  serviceEnd: string | null;
  billedThrough: string | null;
  contingentMode: ContingentMode | null;
  followupTariffId: number | null;
  autoRenew: boolean;
  paymentInstrumentId: number | null;
  collectionBlocked: boolean;
  dunningLevel: number;
  status: 'open' | 'closed';
  openedAt: string | null;
  balanceCents?: number;
}

export interface ContractAccountDetail extends ContractAccount {
  balanceCents: number;
  debtor: { id: number; debtorNumber: string; name: string | null } | null;
  paymentInstrument: PaymentInstrument | null;
  openDebitCents: number;
  openCreditCents: number;
  postings: AccountPosting[];
  /** Kontingent mit Tarif (Phase 5); sonst null. */
  contingent: ContingentSummary | null;
}

export type UnitEntryKind = 'purchase' | 'debit' | 'reversal' | 'adjustment' | 'forfeit';

export interface UnitEntry {
  id: number;
  kind: UnitEntryKind;
  units: number;
  packageId: number | null;
  lessonId: number | null;
  lessonDate: string | null;
  lessonStatus: string | null;
  description: string;
  reversesEntryId: number | null;
  createdAt: string | null;
}

export interface ContingentInstallment {
  number: number;
  dueDate: string;
  amountCents: number;
  posted: boolean;
}

export interface ContingentPackage {
  id: number;
  tariffId: number | null;
  name: string;
  units: number;
  priceCents: number;
  paymentMode: 'prepaid' | 'installments';
  installmentCount: number | null;
  source: 'enrollment' | 'followup' | 'office' | 'portal';
  purchasedOn: string;
  installments: ContingentInstallment[];
}

export interface ContingentSummary {
  unitBalance: number;
  autoRenew: boolean;
  followupTariffId: number | null;
  closedAt: string | null;
  packages: ContingentPackage[];
  entries: UnitEntry[];
}

export interface ContingentDetail extends ContingentSummary {
  availableTariffs: Tariff[];
}

export interface ContingentTariffOption {
  id: number;
  name: string;
  priceCents: number;
  units: number | null;
  contingentMode: ContingentMode | null;
  installmentCount: number | null;
}

export interface ContingentJobSummary {
  installments: { installments: number; totalCents: number };
  lessons: { lessons: number; debits: number; reversals: number };
  renewals: number;
  usage: Record<string, { accounts: number; totalCents: number }>;
  closed: { accounts: number; forfeitedUnits: number; refundCents: number; extended: number };
}

export interface EnrollmentBillingOptions {
  model: BillingModel | null;
  error: { code: string; message: string } | null;
  tariff?: {
    id: number;
    name: string;
    priceCents: number;
    pricePeriodMonths: number | null;
    units: number | null;
    contingentMode: ContingentMode | null;
    installmentCount?: number | null;
  } | null;
  allowedIntervals?: number[];
  defaultInterval?: number;
  /** Buchbare Kontingent-Pakete; leer = Altpakete. */
  contingentTariffs?: ContingentTariffOption[];
}

export interface PeriodicChargesSummary {
  through: string;
  accounts: number;
  charges: number;
  totalCents: number;
}

export type ManualPostingKind = 'charge' | 'fee' | 'credit' | 'write_off';

export interface ManualPostingInput {
  kind: ManualPostingKind;
  amountCents: number;
  description: string;
  dueDate?: string;
  idempotencyKey: string;
}

export type PaymentRunStatus =
  | 'draft'
  | 'approved'
  | 'exported'
  | 'submitted'
  | 'settled'
  | 'cancelled';
export type PaymentOrderStatus =
  | 'planned'
  | 'submitted'
  | 'settled'
  | 'failed'
  | 'returned'
  | 'cancelled';

export interface PaymentRun {
  id: number;
  billingEntityId: number;
  provider: string;
  direction: 'debit' | 'credit';
  runNumber: string;
  collectionDate: string;
  status: PaymentRunStatus;
  itemCount: number;
  totalCents: number;
  createdBy: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  submittedAt: string | null;
  fileName: string | null;
  fileSha256: string | null;
  exportedAt: string | null;
  exportedBy: string | null;
  bankOrderId: string | null;
  bankStatus: 'transmitted' | 'processed' | 'rejected' | null;
}

export interface PaymentOrder {
  id: number;
  debtorId: number;
  debtorNumber: string | null;
  debtorName: string | null;
  /** Kontoinhaber des Mandats — bei Kindern nicht derselbe wie der Lernende. */
  payerName: string | null;
  /** Für wen eingezogen wird; ein Auftrag kann Geschwister bündeln. */
  studentNames: string[];
  paymentInstrumentId: number | null;
  sepaMandateId: number | null;
  sequenceType: string | null;
  preNotifiedAt: string | null;
  endToEndId: string;
  amountCents: number;
  remittanceText: string;
  status: PaymentOrderStatus;
  providerRef: string | null;
  failureReason: string | null;
  returnReasonCode: string | null;
  settledAt: string | null;
  returnedAt: string | null;
}

export interface PaymentRunDetail extends PaymentRun {
  orders: PaymentOrder[];
  preNotifications?: PreNotificationResult | null;
  /** Einheit überträgt per EBICS und der Zugang ist aktiv. */
  ebicsReady?: boolean;
}

export type EbicsStatus = 'new' | 'keys_sent' | 'bank_keys_pending' | 'active' | 'suspended';

export interface EbicsConnection {
  id: number;
  billingEntityId: number;
  ebicsVersion: 'H005' | 'H004';
  bankUrl: string;
  bankName: string;
  hostId: string;
  partnerId: string;
  userId: string;
  systemId: string | null;
  signatureClass: 'T';
  status: EbicsStatus;
  userKeyHashes: Record<string, string>;
  bankKeyHashes: Record<string, string>;
  keysSentAt: string | null;
  activatedAt: string | null;
  lastFetchAt: string | null;
  lastError: string | null;
}

export interface EbicsConnectionInput {
  ebicsVersion: 'H005' | 'H004';
  bankUrl: string;
  bankName: string;
  hostId: string;
  partnerId: string;
  userId: string;
  systemId?: string;
}

export interface EbicsTransfer {
  id: number;
  direction: 'upload' | 'download' | 'key_management';
  orderType: string;
  service: string | null;
  orderId: string | null;
  paymentRunId: number | null;
  fileSha256: string | null;
  fileSize: number | null;
  status: 'ok' | 'no_data' | 'error';
  returnCode: string | null;
  message: string | null;
  createdBy: string | null;
  createdAt: string | null;
}

export interface EbicsOverview {
  connection: EbicsConnection | null;
  transfers: EbicsTransfer[];
  sepaTransport: SepaTransport;
}

export interface EbicsFetchResult {
  statements: {
    files: number;
    entries: number;
    matched: number;
    unmatched: number;
    alreadyImported: number;
    error?: string | null;
  };
  protocol: { updated: number; error?: string };
}

export interface PreNotificationResult {
  sent: number;
  failed: number;
  missingEmail: number;
}

export type MandateStatus = 'active' | 'revoked' | 'expired' | 'superseded';

export interface SepaMandate {
  id: number;
  billingEntityId: number;
  debtorId: number;
  paymentInstrumentId: number;
  reference: string;
  accountHolder: string;
  ibanMasked: string;
  bic: string | null;
  signedOn: string;
  channel: 'website' | 'portal' | 'office' | 'contract_adjustment';
  status: MandateStatus;
  lastCollectedOn: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  supersededById: number | null;
  createdAt: string | null;
  mandateText?: string;
}

export interface MandateEntityOption {
  id: number;
  name: string;
  isActive: boolean;
  sepaProblem: string | null;
  preNotificationDays: number;
}

export interface MandateInput {
  billingEntityId: number;
  accountHolder: string;
  iban: string;
  bic?: string;
  signedOn?: string;
  accountIds?: number[];
}

export type RefundStatus = 'open' | 'in_run' | 'paid' | 'failed' | 'cancelled';

export interface RefundRequest {
  id: number;
  billingEntityId: number;
  contractAccountId: number;
  debtorId: number;
  amountCents: number;
  recipientName: string;
  ibanMasked: string;
  bic: string | null;
  remittanceText: string;
  status: RefundStatus;
  paymentOrderId: number | null;
  failureReason: string | null;
  paidAt: string | null;
  createdAt: string | null;
}

export interface RefundInput {
  amountCents: number;
  recipientName?: string;
  iban?: string;
  bic?: string;
  remittance?: string;
  idempotencyKey: string;
}

export interface BankStatementImport {
  id: number;
  billingEntityId: number;
  fileName: string;
  statementIds: string[];
  entryCount: number;
  matchedCount: number;
  unmatchedCount: number;
  duplicateCount: number;
  importedBy: string | null;
  createdAt: string | null;
  alreadyImported?: boolean;
}

export type BankEventStatus = 'unmatched' | 'needs_review' | 'matched' | 'ignored';

export interface BankEvent {
  id: number;
  billingEntityId: number | null;
  importId: number | null;
  eventType: string;
  matchStatus: BankEventStatus;
  amountCents: number | null;
  bookingDate: string | null;
  valueDate: string | null;
  counterpartyName: string | null;
  counterpartyIban: string | null;
  remittance: string | null;
  endToEndId: string | null;
  returnReason: string | null;
  paymentOrderId: number | null;
  contractAccountId: number | null;
  note: string | null;
  processedAt: string | null;
}

export interface SepaSettleSummary {
  runs: number;
  settledOrders: number;
  expiredMandates: number;
}

export interface PaymentRunList {
  items: PaymentRun[];
  providers: string[];
  entities: Array<{ id: number; name: string; isActive: boolean }>;
}

/** Datei mit Anmeldung laden; der Dateiname kommt aus Content-Disposition. */
export async function downloadFile(path: string, fallbackName: string): Promise<{ blob: Blob; filename: string }> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE_URL}${path}`, { method: 'GET', credentials: 'include', headers });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = (await res.json()) as { error?: { message?: string } };
      message = data.error?.message || message;
    } catch {
      /* Antwort ohne JSON */
    }
    throw new Error(message);
  }
  const disposition = res.headers.get('content-disposition') || '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  return { blob: await res.blob(), filename: match?.[1] || fallbackName };
}

/** Blob im Browser als Datei speichern. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const query = (params: Record<string, string | number | boolean | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
};

export const billingApi = {
  // Einstellungen und abrechnende Einheiten
  getSettings: () => api.get<BillingSettings>(`${BASE}/settings`),
  saveSettings: (settings: BillingSettings) =>
    api.put<BillingSettings>(`${BASE}/settings`, settings),
  listEntities: () => api.get<{ items: BillingEntity[] }>(`${BASE}/entities`),
  createEntity: (input: BillingEntityInput) => api.post<BillingEntity>(`${BASE}/entities`, input),
  updateEntity: (id: number, input: BillingEntityInput) =>
    api.put<BillingEntity>(`${BASE}/entities/${id}`, input),
  saveCredentials: (id: number, provider: string, values: StripeCredentialsInput) =>
    api.put<ProviderFlags>(`${BASE}/entities/${id}/credentials/${provider}`, values),
  assignStandort: (standortId: number, billingEntityId: number) =>
    api.put<{ standortId: number; billingEntityId: number }>(
      `${BASE}/standorte/${standortId}/entity`,
      { billingEntityId },
    ),

  // Tarife
  listTariffs: (params: { unterrichtsformId?: number; standortId?: number; includeInactive?: boolean } = {}) =>
    api.get<{ items: Tariff[] }>(`${BASE}/tariffs${query(params)}`),
  createTariff: (input: TariffInput) => api.post<Tariff>(`${BASE}/tariffs`, input),
  importLegacyTariffs: (input: {
    unterrichtsformId?: number;
    standortId?: number;
    validFrom?: string;
    dryRun?: boolean;
  }) => api.post<LegacyTariffImport>(`${BASE}/tariffs/import-legacy`, input),
  updateTariff: (id: number, input: { name?: string; validTo?: string | null; isActive?: boolean }) =>
    api.put<Tariff>(`${BASE}/tariffs/${id}`, input),

  // Vertragskonten und Zahlungsmittel
  listAccounts: (params: { personId?: number | string; enrollmentId?: number; q?: string; billingEntityId?: number }) =>
    api.get<{ items: Array<ContractAccount & { debtorName?: string | null; debtorNumber?: string | null }> }>(`${BASE}/contract-accounts${query(params)}`),
  getAccount: (id: number) => api.get<ContractAccountDetail>(`${BASE}/contract-accounts/${id}`),
  openAccount: (enrollmentId: number, options: { intervalMonths?: number; tariffId?: number } = {}) =>
    api.post<ContractAccountDetail>(`${BASE}/contract-accounts`, { enrollmentId, ...options }),
  accountOptions: (enrollmentId: number) =>
    api.get<EnrollmentBillingOptions>(`${BASE}/enrollments/${enrollmentId}/account-options`),
  updateAccount: (
    id: number,
    input: {
      paymentInstrumentId?: number | null;
      collectionBlocked?: boolean;
      intervalMonths?: number;
      serviceEnd?: string | null;
    },
  ) =>
    api.put<ContractAccountDetail>(`${BASE}/contract-accounts/${id}`, input),
  postToAccount: (id: number, input: ManualPostingInput) =>
    api.post<ContractAccountDetail>(`${BASE}/contract-accounts/${id}/postings`, input),
  reversePosting: (id: number, description?: string) =>
    api.post<ContractAccountDetail>(`${BASE}/postings/${id}/reverse`, description ? { description } : {}),
  listInstruments: (personId: number | string) =>
    api.get<{ items: PaymentInstrument[]; availableStripeMethods: StripeMethodOption[] }>(
      `${BASE}/persons/${personId}/instruments`,
    ),
  createInstrument: (personId: number | string, input: { provider: 'invoice' | 'stripe'; paymentMethodId?: string }) =>
    api.post<PaymentInstrument>(`${BASE}/persons/${personId}/instruments`, input),

  // Belege (Phase 7)
  listDocuments: (
    params: {
      contractAccountId?: number;
      billingEntityId?: number;
      month?: string;
      status?: string;
      limit?: number;
    } = {},
  ) => api.get<{ items: BillingDocument[] }>(`${BASE}/documents${query(params)}`),
  getDocument: (id: number) => api.get<BillingDocument>(`${BASE}/documents/${id}`),
  downloadDocument: (id: number, fileName: string) =>
    downloadFile(`${BASE}/documents/${id}/file`, fileName),
  cancelDocument: (id: number, reason: string) =>
    api.post<BillingDocument>(`${BASE}/documents/${id}/cancel`, { reason }),
  runDocuments: (input: { month?: string; billingEntityId?: number; contractAccountId?: number } = {}) =>
    api.post<DocumentRunSummary>(`${BASE}/jobs/documents`, input),

  // DATEV (Phase 7)
  downloadDatevExport: (params: { from: string; to: string; billingEntityId?: number }) =>
    downloadFile(`${BASE}/datev/export${query(params)}`, 'EXTF_Buchungsstapel.csv'),

  // Mahnwesen (Phase 7)
  listDunningNotices: (
    params: { status?: string; level?: number; contractAccountId?: number; limit?: number } = {},
  ) => api.get<{ items: DunningNotice[] }>(`${BASE}/dunning-notices${query(params)}`),
  getDunningNotice: (id: number) => api.get<DunningNotice>(`${BASE}/dunning-notices/${id}`),
  issueDunningNotice: (id: number, send = true) =>
    api.post<DunningNotice>(`${BASE}/dunning-notices/${id}/issue`, { send }),
  sendDunningNotice: (id: number) =>
    api.post<DunningNotice>(`${BASE}/dunning-notices/${id}/send`, {}),
  cancelDunningNotice: (id: number, reason: string) =>
    api.post<DunningNotice>(`${BASE}/dunning-notices/${id}/cancel`, { reason }),
  downloadDunningNotice: (id: number, fileName: string) =>
    downloadFile(`${BASE}/dunning-notices/${id}/file`, fileName),
  runDunning: (input: { on?: string; contractAccountId?: number } = {}) =>
    api.post<DunningRunSummary>(`${BASE}/jobs/dunning`, input),

  // Beiträge und Anmelde-Wizard (Phase 2)
  runPeriodicCharges: (input: { through?: string; accountId?: number } = {}) =>
    api.post<PeriodicChargesSummary>(`${BASE}/jobs/periodic-charges`, input),
  enrollmentOptions: (params: { unterrichtsformId?: number; standortId?: number; startDate?: string }) =>
    api.get<EnrollmentBillingOptions>(`${BASE}/enrollment-options${query(params)}`),

  // Kontingent auf dem Vertragskonto (Phase 5)
  getContingent: (accountId: number) =>
    api.get<ContingentDetail>(`${BASE}/contract-accounts/${accountId}/contingent`),
  updateContingent: (accountId: number, input: { followupTariffId?: number | null; autoRenew?: boolean }) =>
    api.put<ContractAccountDetail>(`${BASE}/contract-accounts/${accountId}/contingent`, input),
  buyPackage: (accountId: number, input: { tariffId: number; purchasedOn?: string; idempotencyKey: string }) =>
    api.post<ContractAccountDetail>(`${BASE}/contract-accounts/${accountId}/contingent/packages`, input),
  adjustUnits: (accountId: number, input: { units: number; description: string; idempotencyKey: string }) =>
    api.post<ContractAccountDetail>(`${BASE}/contract-accounts/${accountId}/contingent/adjustments`, input),
  runContingentJob: (input: { on?: string } = {}) =>
    api.post<ContingentJobSummary>(`${BASE}/jobs/contingent`, input),

  // Zahlungsläufe
  listRuns: (
    params: { status?: string; billingEntityId?: number; von?: string; bis?: string; limit?: number } = {},
  ) =>
    api.get<PaymentRunList>(`${BASE}/payment-runs${query(params)}`),
  createRun: (input: { billingEntityId: number; provider: string; collectionDate: string }) =>
    api.post<PaymentRunDetail>(`${BASE}/payment-runs`, input),
  getRun: (id: number) => api.get<PaymentRunDetail>(`${BASE}/payment-runs/${id}`),
  approveRun: (id: number) => api.post<PaymentRunDetail>(`${BASE}/payment-runs/${id}/approve`, {}),
  submitRun: (id: number) => api.post<PaymentRunDetail>(`${BASE}/payment-runs/${id}/submit`, {}),
  cancelRun: (id: number) => api.post<PaymentRunDetail>(`${BASE}/payment-runs/${id}/cancel`, {}),
  settleOrder: (id: number, bookedOn?: string) =>
    api.post<PaymentOrder>(`${BASE}/payment-orders/${id}/settle`, bookedOn ? { bookedOn } : {}),
  returnOrder: (id: number, input: { reasonCode: string; feeCents?: number; bookedOn?: string }) =>
    api.post<PaymentOrder>(`${BASE}/payment-orders/${id}/return`, input),

  // Hausbank-Lastschrift (Phase 3): Mandate
  listMandates: (personId: number | string) =>
    api.get<{ items: SepaMandate[]; entities: MandateEntityOption[] }>(
      `${BASE}/persons/${personId}/mandates`,
    ),
  createMandate: (personId: number | string, input: MandateInput) =>
    api.post<SepaMandate>(`${BASE}/persons/${personId}/mandates`, input),
  getMandate: (id: number) => api.get<SepaMandate>(`${BASE}/mandates/${id}`),
  revokeMandate: (id: number, reason?: string) =>
    api.post<SepaMandate>(`${BASE}/mandates/${id}/revoke`, reason ? { reason } : {}),
  mandateText: (billingEntityId: number) =>
    api.get<{ text: string; sepaProblem: string | null }>(
      `${BASE}/mandate-text${query({ billingEntityId })}`,
    ),

  // Bankdateien
  exportRun: (id: number) => api.post<PaymentRunDetail>(`${BASE}/payment-runs/${id}/export`, {}),
  confirmUpload: (id: number) =>
    api.post<PaymentRunDetail>(`${BASE}/payment-runs/${id}/confirm-upload`, {}),
  sendPreNotifications: (id: number) =>
    api.post<PaymentRunDetail>(`${BASE}/payment-runs/${id}/pre-notifications`, {}),
  downloadRunFile: (id: number) =>
    downloadFile(`${BASE}/payment-runs/${id}/file`, `zahlungslauf-${id}.xml`),
  runSepaSettle: () => api.post<SepaSettleSummary>(`${BASE}/jobs/sepa-settle`, {}),

  // EBICS (Phase 4)
  getEbics: (entityId: number) => api.get<EbicsOverview>(`${BASE}/entities/${entityId}/ebics`),
  saveEbics: (entityId: number, input: EbicsConnectionInput) =>
    api.put<EbicsConnection>(`${BASE}/entities/${entityId}/ebics`, input),
  ebicsSendKeys: (id: number) => api.post<EbicsConnection>(`${BASE}/ebics/${id}/send-keys`, {}),
  ebicsFetchBankKeys: (id: number) =>
    api.post<EbicsConnection>(`${BASE}/ebics/${id}/fetch-bank-keys`, {}),
  ebicsActivate: (id: number, input: { hashesConfirmed: boolean; useForTransport: boolean }) =>
    api.post<EbicsConnection>(`${BASE}/ebics/${id}/activate`, input),
  ebicsSuspend: (id: number) => api.post<EbicsConnection>(`${BASE}/ebics/${id}/suspend`, {}),
  ebicsResume: (id: number) => api.post<EbicsConnection>(`${BASE}/ebics/${id}/resume`, {}),
  ebicsReset: (id: number) => api.post<EbicsConnection>(`${BASE}/ebics/${id}/reset`, {}),
  ebicsFetch: (id: number) => api.post<EbicsFetchResult>(`${BASE}/ebics/${id}/fetch`, {}),
  downloadIniLetter: (id: number) => downloadFile(`${BASE}/ebics/${id}/ini-letter`, `INI-Brief-${id}.pdf`),
  transmitRun: (id: number) => api.post<PaymentRunDetail>(`${BASE}/payment-runs/${id}/transmit`, {}),
  resetTransmission: (id: number) =>
    api.post<PaymentRunDetail>(`${BASE}/payment-runs/${id}/reset-transmission`, {}),

  // Erstattungen
  listRefunds: (params: { status?: string; contractAccountId?: number; billingEntityId?: number } = {}) =>
    api.get<{ items: RefundRequest[] }>(`${BASE}/refunds${query(params)}`),
  createRefund: (accountId: number, input: RefundInput) =>
    api.post<RefundRequest>(`${BASE}/contract-accounts/${accountId}/refunds`, input),
  cancelRefund: (id: number) => api.post<RefundRequest>(`${BASE}/refunds/${id}/cancel`, {}),
  createRefundRun: (input: { billingEntityId: number; executionDate: string }) =>
    api.post<PaymentRunDetail>(`${BASE}/refund-runs`, input),

  // Kontoauszüge
  listBankStatements: () => api.get<{ items: BankStatementImport[] }>(`${BASE}/bank-statements`),
  uploadBankStatement: (file: File, billingEntityId?: number) => {
    const form = new FormData();
    form.append('file', file);
    if (billingEntityId) form.append('billingEntityId', String(billingEntityId));
    return api.upload<BankStatementImport>(`${BASE}/bank-statements`, form);
  },
  listBankEvents: (params: { status?: BankEventStatus | 'all'; importId?: number } = {}) =>
    api.get<{ items: BankEvent[] }>(`${BASE}/bank-events${query(params)}`),
  assignBankEvent: (id: number, contractAccountId: number) =>
    api.post<BankEvent>(`${BASE}/bank-events/${id}/assign`, { contractAccountId }),
  ignoreBankEvent: (id: number, note?: string) =>
    api.post<BankEvent>(`${BASE}/bank-events/${id}/ignore`, note ? { note } : {}),
};
